import type { Country } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  LargeDropdown,
  LargeDropdownCaret,
  LargeDropdownCurrentSelection,
  LargeDropdownOption,
  LargeDropdownSelect,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEventHandler } from "react"
import {
  useCallback,
  useMemo,
  useRef,
} from "react"

import { usePolyglot } from "src/contexts"
import { useCountries } from "src/hooks"

interface Option {
  value: string;
  children: string;
  disabled?: boolean;
}

const id = "country-of-residence-select"
const polyglotPrefix = "components.country_of_residence."

export const defaultCountryValue = ""

interface Props {
  className?: string;
  value: string;
  onChange: (c?: Country) => void;
}

export function CountryOfResidenceSelect(props: Props) {
  const {
    className,
    value,
    onChange: propsOnChange,
  } = props

  const polyglot = usePolyglot()
  const countries = useCountries().data
  const selectRef = useRef<HTMLSelectElement>(null)

  const options: Array<Option> = useMemo(
    () => {
      const optionsArray: Array<Option> = [
        // the placeholder option
        {
          value: defaultCountryValue,
          children: polyglot.t(`${polyglotPrefix}placeholder`),
          disabled: true,
        },
      ]

      const countryOptions = (countries ?? [])
        .filter(
          (country): boolean => country.is_supported,
        )
        .map(
          (country): Option => ({
            value: country.country_code,
            children: polyglot.t(`country.${country.country_code}`),
          }),
        )
        .sort(
          (optionA, optionB) => optionA.children.localeCompare(optionB.children),
        )

      optionsArray.push(...countryOptions)
      return optionsArray
    },
    [
      polyglot,
      countries,
    ],
  )

  const onChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
    (e) => {
      const countryCode = e.target.value
      const country = (countries ?? []).find((c) => c.country_code === countryCode)

      if (country?.is_supported) {
        selectRef.current?.setCustomValidity("")
      } else {
        selectRef.current?.setCustomValidity(polyglot.t(`${polyglotPrefix}unsupported_error`))
      }

      propsOnChange(country)
    },
    [
      polyglot,
      countries,
      propsOnChange,
      selectRef,
    ],
  )

  return (
    <LargeDropdown className={className}>
      <LargeDropdownSelect
        id={id}
        value={value}
        onChange={onChange}
        ref={selectRef}
        autoComplete="country-name"
        required
      >
        {options.map(
          (selectOption) => (
            <LargeDropdownOption
              key={selectOption.value}
              {...selectOption}
            />
          ),
        )}
      </LargeDropdownSelect>
      <LargeDropdownCurrentSelection />
      <LargeDropdownCaret />
    </LargeDropdown>
  )
}
